import { useCallback, useEffect, useRef, useState } from 'react';
import api, { apiError } from '@/lib/api';
import { newRequestId } from '@/components/casino/dice/diceModel';
import { cleanBalances, confirmedCommand, confirmedRound, pendingKey, validBet, validPending } from './blackjackModel';

// Persist unresolved request settings only. The shoe, hand state and ledger stay on the server.
export default function useBlackjackGame(userId, refreshWallet) {
  const [legacy, setLegacy] = useState(null);
  const [round, setRound] = useState(null), [history, setHistory] = useState([]), [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true), [busy, setBusy] = useState(false), [error, setError] = useState(''), [syncError, setSyncError] = useState(''), [historyError, setHistoryError] = useState(false);
  const generation = useRef(0), identity = useRef(userId), pending = useRef(null), current = useRef(null), lock = useRef(false), latestId = useRef(null), corrupt = useRef(false);
  identity.current = userId;
  const invalidate = useCallback(() => { generation.current += 1; }, []);
  const refreshRef = useRef(refreshWallet); refreshRef.current = refreshWallet;
  const key = pendingKey(userId);
  const live = useCallback(gen => generation.current === gen && identity.current === userId, [userId]);
  const wallet = useCallback(value => { const balances = cleanBalances(value); try { Promise.resolve(refreshRef.current?.(balances)).catch(() => {}); } catch { /* no financial replay */ } }, []);
  const clear = useCallback(requestId => {
    try { if (JSON.parse(sessionStorage.getItem(key))?.body?.request_id === requestId) sessionStorage.removeItem(key); } catch { /* server recovery remains authoritative */ }
    if (pending.current?.body.request_id === requestId) pending.current = null;
  }, [key]);
  const rememberRound = useCallback(value => {
    const old = current.current;
    if (old?.round_id === value.round_id && old.revision > value.revision) return false;
    current.current = value; setRound(value);
    if (value.phase === 'complete') setHistory(rows => [value, ...rows.filter(row => row.round_id !== value.round_id)].slice(0, 30));
    return true;
  }, []);
  const accept = useCallback((data, request, gen) => {
    if (!live(gen) || latestId.current !== request.body.request_id) return null;
    const confirmed = confirmedCommand(data, request);
    if (!rememberRound(confirmed.round)) return null;
    clear(request.body.request_id); corrupt.current = false; setError(''); setSyncError(''); wallet(confirmed.balances);
    return confirmed;
  }, [clear, live, rememberRound, wallet]);
  const loadHistory = useCallback(async () => {
    const gen = generation.current;
    try {
      const { data } = await api.get('/casino/blackjack/history');
      if (!Array.isArray(data.rounds)) throw new Error();
      const rows = data.rounds.map(row => confirmedRound(row));
      if (live(gen)) { const latest = current.current; setHistory(latest?.phase === 'complete' ? [latest, ...rows.filter(row => row.round_id !== latest.round_id)].slice(0, 30) : rows); setHistoryError(false); }
    } catch { if (live(gen)) setHistoryError(true); }
  }, [live]);
  const readActive = useCallback(async gen => {
    const { data } = await api.get('/casino/blackjack/active');
    if (!live(gen)) return;
    if (data.round) rememberRound(confirmedRound(data.round));
    setLegacy(data.legacy || null);
    if (data.balances) wallet(data.balances);
    if (!corrupt.current) setSyncError('');
  }, [live, rememberRound, wallet]);
  const reconcile = useCallback(async (request, gen) => {
    const { data } = await api.get(`/casino/blackjack/command/${encodeURIComponent(request.body.request_id)}`);
    if (!live(gen) || latestId.current !== request.body.request_id) return null;
    if (data.request_id !== request.body.request_id) throw new Error('Wrong action reference');
    if (data.status === 'completed') return accept(data, request, gen);
    if (data.status === 'not_placed' && data.round == null && data.presentation == null && data.balances == null) {
      clear(request.body.request_id); setSyncError(''); setError('This action was not placed. Your hand has been refreshed.'); await readActive(gen); return null;
    }
    throw new Error('Your action is still awaiting confirmation');
  }, [accept, clear, live, readActive]);
  const connect = useCallback(async () => {
    if (lock.current || identity.current !== userId) return;
    const gen = generation.current; lock.current = true; setLoading(true);
    try {
      try {
        const { data } = await api.get('/casino/blackjack/config');
        if (data.decks !== 6 || data.max_hands !== 4 || data.min_bet !== 1 || data.max_bet !== 1000 || data.blackjack_payout !== '3:2' || data.dealer_stands_soft_17 !== true) throw new Error();
        if (live(gen)) setConfig(data);
      } catch { if (live(gen)) { setConfig(null); setError('Couldn’t load Blackjack rules. Reconnect before dealing.'); } }
      if (!live(gen)) return;
      try { if (pending.current) await reconcile(pending.current, gen); else await readActive(gen); }
      catch { if (live(gen)) setSyncError('Your last action needs confirmation. Resume the same action or reconnect before playing.'); }
      await loadHistory();
    } finally { if (live(gen)) { lock.current = false; setLoading(false); } }
  }, [live, loadHistory, readActive, reconcile, userId]);
  useEffect(() => {
    invalidate(); pending.current = null; current.current = null; latestId.current = null; lock.current = false; corrupt.current = false;
    setRound(null); setLegacy(null); setHistory([]); setConfig(null); setBusy(false); setLoading(true); setError(''); setSyncError(''); setHistoryError(false);
    try {
      const saved = JSON.parse(sessionStorage.getItem(key));
      if (saved && !validPending(saved)) throw new Error();
      pending.current = saved || null; latestId.current = saved?.body.request_id || null;
    } catch { corrupt.current = true; setSyncError('Your saved action needs recovery. Reconnect, or contact support if it remains unconfirmed.'); }
    connect();
    return invalidate;
  }, [connect, invalidate, key]);
  const send = useCallback(async request => {
    if (lock.current || identity.current !== userId) return null;
    const gen = generation.current; lock.current = true; latestId.current = request.body.request_id; setBusy(true); setError('');
    try {
      const { data } = await api.post(`/casino/blackjack/${request.kind === 'start' ? 'start-v2' : 'action'}`, request.body, { timeout: 15000 });
      return accept(data, request, gen);
    } catch (err) {
      if (!live(gen)) return null;
      setError(err?.response ? apiError(err.response.data?.detail) : 'Connection interrupted. Checking the same action…');
      // A validation response is produced before the handler can mutate anything.
      if (err?.response?.status === 422) { clear(request.body.request_id); await readActive(gen); return null; }
      try { return await reconcile(request, gen); }
      catch { if (live(gen)) setSyncError('Your action is unconfirmed. Resume the same action before doing anything else.'); return null; }
    } finally { if (live(gen)) { lock.current = false; setBusy(false); } }
  }, [accept, clear, live, readActive, reconcile, userId]);
  const submit = useCallback(async (kind, settings) => {
    if (lock.current || loading || syncError || pending.current || identity.current !== userId) return null;
    const before = current.current;
    if (kind === 'start' && (!config || config.enabled === false || legacy || before && before.phase !== 'complete' || !validBet(settings.cents))) return null;
    if (kind === 'action' && (!before || before.phase === 'complete' || !before.actions[settings.action])) return null;
    const request = kind === 'start' ? { kind, currency: settings.currency, body: { request_id: newRequestId(), currency: settings.currency, bet: settings.cents / 100 } } :
      { kind, currency: before.currency, before_stake: before.total_stake_cents, before_hand_stake: before.hands[before.active_hand].stake_cents, bet_cents: before.bet_cents,
        body: { request_id: newRequestId(), round_id: before.round_id, revision: before.revision, action: settings.action } };
    try { if (!validPending(request)) throw new Error(); sessionStorage.setItem(key, JSON.stringify(request)); }
    catch { setError('Couldn’t save your action reference. Check browser storage before playing.'); return null; }
    pending.current = request;
    return send(request);
  }, [config, key, legacy, loading, send, syncError, userId]);
  const resume = useCallback(async () => { if (lock.current || loading) return null; if (pending.current) return send(pending.current); await connect(); return null; }, [connect, loading, send]);
  return { round, legacy, history, config, loading, busy, error, syncError, historyError, pending: pending.current,
    start: settings => submit('start', settings), act: action => submit('action', { action }), resume, reconnect: connect, loadHistory };
}
